import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Maximize2,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from "lucide-react";

const pages = [
  "/dinnerMenu/page1.jpg",
  "/dinnerMenu/page2.jpg",
  "/dinnerMenu/page3.jpg",
  "/dinnerMenu/page4.jpg",
  "/dinnerMenu/page5.jpg",
  "/dinnerMenu/page6.jpg",
  "/dinnerMenu/page7.jpg",
  "/dinnerMenu/page8.jpg",
];

const DinnerMenu = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [zoom, setZoom] = useState(1);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % pages.length);
    setZoom(1);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev === 0 ? pages.length - 1 : prev - 1));
    setZoom(1);
  };

  // Keyboard support
  useEffect(() => {
    const handleKey = (e) => {
      if (activeIndex === null) return;

      if (e.key === "Escape") setActiveIndex(null);
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(z + 0.2, 3));
      if (e.key === "-") setZoom((z) => Math.max(z - 0.2, 1));
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [activeIndex]);

  return (
    <div className="min-h-screen bg-darkbg text-white">
      {/* HERO */}
      <section className="relative text-center pt-40 pb-32 bg-gradient-to-r from-black via-black/80 to-navy px-6">
        <h1 className="text-4xl md:text-6xl font-serif tracking-widest text-neonYellow uppercase animate-neonFlicker ">
          Dinner Menu
        </h1>

        <p className="text-textGray max-w-2xl mx-auto mt-6">
          Experience authentic Vietnamese flavours in an elegant evening
          setting.
        </p>
      </section>

      {/* MENU GRID */}
      <div className="max-w-5xl mx-auto px-4 py-20 space-y-16">
        {pages.map((src, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative group rounded-xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
          >
            {/* Expand Button */}
            <button
              onClick={() => {
                setActiveIndex(index);
                setZoom(1);
              }}
              className="absolute top-4 right-4 z-20 bg-navy hover:bg-navy/80 text-yellowstar p-3 rounded-full opacity-0 group-hover:opacity-100 transition"
            >
              <Maximize2 size={18} />
            </button>

            <img
              src={src}
              alt={`Dinner Menu Page ${index + 1}`}
              className="w-full transition-transform duration-500 group-hover:scale-105"
            />

            <div className="absolute bottom-4 left-4 bg-navy/70 text-sm px-3 py-1 rounded text-gray-300">
              Page {index + 1}
            </div>
          </motion.div>
        ))}
      </div>

      {/* FULLSCREEN VIEWER */}
      <AnimatePresence>
        {activeIndex !== null && (
          <motion.div
            className="fixed inset-0 bg-black/95 z-[999] flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Close */}
            <button
              onClick={() => setActiveIndex(null)}
              className="absolute top-6 right-6 neonYellow bg-black/70 hover:bg-black p-3 rounded-full"
            >
              <X size={24} />
            </button>

            {/* Arrows */}
            {/* <button
              onClick={prevSlide}
              className="absolute left-6 text-white bg-black/60 hover:bg-black p-3 rounded-full"
            >
              <ChevronLeft size={28} />
            </button>

            <button
              onClick={nextSlide}
              className="absolute right-6 text-white bg-black/60 hover:bg-black p-3 rounded-full"
            >
              <ChevronRight size={28} />
            </button> */}

            <button
              onClick={prevSlide}
              className="
    absolute 
    left-2 md:left-6
    top-1/2 -translate-y-1/2
    z-50
    text-white 
    bg-black/60 
    hover:bg-black 
    p-2 md:p-3
    rounded-full
  "
            >
              <ChevronLeft size={22} className="md:w-7 md:h-7" />
            </button>
            <button
              onClick={nextSlide}
              className="
    absolute 
    right-2 md:right-6
    top-1/2 -translate-y-1/2
    z-50
    text-white 
    bg-black/60 
    hover:bg-black 
    p-2 md:p-3
    rounded-full
  "
            >
              <ChevronRight size={22} className="md:w-7 md:h-7" />
            </button>

            {/* Zoom Controls */}
            {/* <div className="absolute bottom-6 flex gap-4 bg-black/60 px-6 py-3 rounded-full z-20">
              <button
                onClick={() => setZoom((z) => Math.min(z + 0.2, 3))}
                className="text-neonYellow "
              >
                <ZoomIn size={22} />
              </button>

              <button
                onClick={() => setZoom((z) => Math.max(z - 0.2, 1))}
                className="text-neonYellow"
              >
                <ZoomOut size={22} />
              </button>

              <button onClick={() => setZoom(1)} className="text-neonYellow">
                <RotateCcw size={22} />
              </button>
            </div> */}

            {/* Scrollable Image Container */}
            <div className="w-full h-full flex items-center justify-center overflow-auto px-6 md:px-20">
              <motion.img
                key={activeIndex}
                src={pages[activeIndex]}
                alt="Fullscreen Dinner Menu"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  transform: `scale(${zoom})`,
                  transition: "transform 0.3s ease",
                }}
                className="
      object-contain
      rounded-lg
      max-h-[85vh]
      md:max-h-[80vh]
      max-w-full
    "
              />
            </div>

            {/* Page Counter */}
            <div className="absolute top-6 left-6 text-gray-400 text-sm">
              {activeIndex + 1} / {pages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DinnerMenu;
